import { NextPage } from "next";
import { useQuery } from "@tanstack/react-query";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import WorkshopsGrid from "@/components/workshop-components/WorkshopsGrid";
import Search from "@/components/classes-page-components/Search";
import Space from "@/components/ui/Space";
import { Skeleton } from "@/components/ui/Skeleton";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";

export async function getServerSideProps({ locale }) {
  return {
    props: {
      locale,
      title: locale === "fa" ? "کارگاه های آموزشی آنلاین | درسون" : "Online Educational Workshops | Darsoon",
      description:
        locale === "fa"
          ? "ورکشاپ های آنلاین درسون با هدف ایجاد محیطی شاد و آموزنده برای کودکان و همچنین توسعه فردی بزرگسالان برگزار می شوند."
          : "Darsoon's interactive online workshops are created to be both fun and informative for children, and to support personal growth in adults.",
    },
  };
}

const PAGE_SIZE = 12;

type Props = {
  title: string;
  description: string;
  locale: string;
};

const Page: NextPage = ({ title, description, locale }: Props) => {
  const supabase = useSupabaseClient();
  const { asPath } = useRouter();
  const baseUrl = "https://darsoon.com";
  const canonicalUrl = `${baseUrl}${locale === "fa" ? "" : "/en"}${asPath}`;

  const workshopQ = useQuery({
    queryKey: ["workshopNewWebsite-1"],
    queryFn: async () => {
      const { data, error }: any = await supabase.from("workshop").select(`*`).order("date", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonicalUrl}
        openGraph={{
          title: title,
          description: description,
          locale: locale === "fa" ? "fa_IR" : "en_US",
          site_name: locale === "fa" ? "درسون" : "Darsoon",
          url: canonicalUrl,
        }}
        additionalLinkTags={[
          {
            rel: "alternate",
            href: `${baseUrl}${asPath}`,
            hrefLang: "fa",
          },
          {
            rel: "alternate",
            href: `${baseUrl}/en${asPath}`,
            hrefLang: "en",
          },
          {
            rel: "alternate",
            href: `${baseUrl}${asPath}`,
            hrefLang: "x-default",
          },
        ]}
      />
      <div className="mx-auto max-w-70rem">
        <h1 className="H1 text-center mb-4">{locale === "fa" ? "ورکشاپ‌ها" : "Workshops"}</h1>
        <div className="h-4"></div>
        <div className="flex flex-col sm:flex-row-reverse sm:gap-6" style={{ gridTemplateColumns: "1fr" }}>
          <div className="grow">
            {workshopQ.isLoading && <CardsGridSkeleton />}
            {workshopQ.data && workshopQ.data.length > 0 && (
              <WorkshopsGrid
                data={workshopQ.data.map((w) => {
                  return {
                    id: w.id,
                    slug: w.slug,
                    title: w.title,
                    titleFa: w.titleFa,
                    from: w.from,
                    to: w.to,
                    subtitle: w.subtitle,
                    subtitleFa: w.subtitleFa,
                    coverPhoto: w.coverPhoto,
                    isFree: w.isFree,
                    teacherFullname: w.teacherFullname,
                    teacherFullnameFa: w.teacherFullnameFa,
                    language: w.language,
                    date: w.date,
                    ageGroup: w.ageGroup,
                    ageGroupFa: w.ageGroupFa,
                    price: w.price,
                  };
                })}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Page;

function CardsGridSkeleton() {
  const { locale } = useRouter();
  return (
    <div className=" lt-sm:max-table-width-in-mobile sm:max-w-main relative">
      <div className="sticky top-0 z-10 b-b-1 b-sand5">
        <div className="relative pb-4 bg-[#FCF9F7] py-4">
          <div className="flex flex-wrap ac gap-8 justify-between bg-[#FCF9F7]">
            <Search
              value=""
              onChange={() => {}}
              placeholder={locale === "en" ? "Search tutors or subjects" : "جستجو بین معلم‌ها و کلاس‌ها"}
            />
          </div>
          <div className="h-10 absolute top-0 w-full translate-y-90% -z-1 px-10">
            <div className=" bg-sand8 opacity-80 blur-20 rd-b-50%  w-full h-full  "></div>
          </div>
        </div>
      </div>
      <Space size="h-19" />
      <ul className="grid gap-6" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(15rem , 100%), 1fr ))" }}>
        {Array.from(Array(PAGE_SIZE).keys()).map((i) => {
          return <ClassCardSkeleton key={i} />;
        })}
      </ul>
    </div>
  );
}

function ClassCardSkeleton() {
  return (
    <li role="presentation">
      <div className="grid">
        <Skeleton className=" rd-xl object-cover aspect-ratio-1/1 w-full" />
      </div>
      <div className="h-3"></div>
      <p className="text-xl skeleton-text w-40%"></p>
      <div className="h-1"></div>
      <div>
        <p className="skeleton-text w-60%"></p>
        <p className="skeleton-text w-40%"></p>
        <div className="flex justify-between">
          <p className="skeleton-text w-40%"></p>
          <p className="skeleton-text w-20%"></p>
        </div>
      </div>
    </li>
  );
}
